import { IBalusterGeometry, IRailingConfig, IRailingGeometry, IStairConfig, IStepGeometry, IVec3 } from '@stair-craft/common/util';

import { balusterCount } from '../shared-baluster-count/baluster-count';
import { buildRadialRailing } from '../spiral-build-radial-railing/build-radial-railing';
import { ISpiralLayout } from '../spiral-calculate-layout/calculate-layout';

/**
 * Moves a radial rail's inner post onto the pole axis, standing on the pole top through the
 * landing, and runs the handrail to it (SC-CALC-091): the rail is tied to the pole, not to a
 * separate post beside it.
 */
function onPoleTop(railing: IRailingGeometry, axis: { x: number; z: number }, poleTop: number): IRailingGeometry {
    const innerPost: IBalusterGeometry = railing.posts[0];
    const top: number = innerPost.position.y + innerPost.height;
    return {
        ...railing,
        posts: [{ ...innerPost, position: { x: axis.x, y: poleTop, z: axis.z }, height: top - poleTop }, ...railing.posts.slice(1)],
        handrail: { ...railing.handrail, points: [{ x: axis.x, y: top, z: axis.z }, ...railing.handrail.points.slice(1)] },
    };
}

/** Helical outer-edge handrail along the steps plus the two radial railings at the exit landing. */
export function generateSpiralRailings(steps: IStepGeometry[], layout: ISpiralLayout, config: IStairConfig): IRailingGeometry[] {
    if (steps.length === 0) {
        return [];
    }

    const rc: IRailingConfig = config.railing;
    const { outerRadius, anglePerStepRad, stepCount, riserHeight } = layout;
    const cx: number = outerRadius;
    const cz: number = outerRadius;
    const clockwise: boolean = config.turnDirection === 'right';
    const dirSign: number = clockwise ? 1 : -1;
    const totalSteps: number = stepCount - 1;

    const railR: number = outerRadius - rc.postWidth / 2;

    const balusters: IBalusterGeometry[] = [];
    const posts: IBalusterGeometry[] = [];
    const handrailPts: IVec3[] = [];

    // Bottom post
    const startAngle: number = 0;
    posts.push({
        position: { x: cx + railR * Math.sin(startAngle), y: 0, z: cz + railR * Math.cos(startAngle) },
        height: riserHeight + rc.height,
        rotation: 0,
    });

    // Handrail points — multiple per step for smooth helix
    const pointsPerStep: number = 4;

    // Every tread takes the same balusters, evenly along its arc, as many as keep the gap within the spacing.
    const perTread: number = balusterCount(railR * anglePerStepRad, rc);
    for (let bi: number = 0; bi < totalSteps * perTread; bi++) {
        const si: number = Math.floor(bi / perTread);
        const stepFrac: number = si + ((bi % perTread) + 0.5) / perTread;
        const balAngle: number = stepFrac * anglePerStepRad * dirSign;
        const baseY: number = (si + 1) * riserHeight;
        const handrailY: number = (stepFrac + 1) * riserHeight + rc.height;
        balusters.push({
            position: { x: cx + railR * Math.sin(balAngle), y: baseY, z: cz + railR * Math.cos(balAngle) },
            height: Math.max(0, handrailY - baseY),
            rotation: 0,
        });
    }

    for (let i: number = 0; i < totalSteps; i++) {
        // Handrail sub-points
        for (let p: number = 0; p < pointsPerStep; p++) {
            const frac: number = i + p / pointsPerStep;
            const angle: number = frac * anglePerStepRad * dirSign;
            const y: number = (frac + 1) * riserHeight;
            handrailPts.push({
                x: cx + railR * Math.sin(angle),
                y: y + rc.height,
                z: cz + railR * Math.cos(angle),
            });
        }
    }

    // Final handrail point at last step
    const endAngle: number = totalSteps * anglePerStepRad * dirSign;
    const topY: number = config.floorHeight + config.floorThickness;
    handrailPts.push({
        x: cx + railR * Math.sin(endAngle),
        y: topY + rc.height,
        z: cz + railR * Math.cos(endAngle),
    });

    // Top post
    posts.push({
        position: { x: cx + railR * Math.sin(endAngle), y: topY, z: cz + railR * Math.cos(endAngle) },
        height: rc.height,
        rotation: 0,
    });

    const result: IRailingGeometry[] = [
        {
            balusters,
            posts,
            side: 'right',
            handrail: { points: handrailPts, width: rc.handrailWidth, height: rc.handrailHeight },
            balusterWidth: rc.balusterWidth,
            postWidth: rc.postWidth,
        },
    ];

    // Radial railings at exit landing edges (part of stair railing, not upper)
    const totalAngle: number = (layout.totalRotationDeg * Math.PI) / 180;
    const exitAng: number = totalAngle * dirSign;
    const openingR: number = Math.min(config.openingWidth, config.openingLength) / 2;
    // The outer end post stands on the slab past the round hole, where the upper ring ends too.
    const exitEdgeR: number = Math.max(outerRadius - rc.postWidth / 2, openingR + rc.postWidth / 2);
    const poleEdge: number = layout.innerRadius + rc.postWidth / 2;
    const floorY: number = config.floorHeight + config.floorThickness;

    // Near edge of landing (at exitAngle) — connects stair top to opening edge. It starts at the
    // stair rail's top post, so it has no inner post of its own.
    const nearEdge: IRailingGeometry = buildRadialRailing(cx, cz, exitAng, railR, exitEdgeR, floorY, rc);
    nearEdge.posts = nearEdge.posts.slice(1);
    result.push(nearEdge);
    // Far edge of landing (at exitAngle + 90°) — from pole to opening edge
    const farAng: number = exitAng + (Math.PI / 2) * dirSign;
    result.push(onPoleTop(buildRadialRailing(cx, cz, farAng, poleEdge, exitEdgeR, floorY, rc), { x: cx, z: cz }, stepCount * riserHeight));

    return result;
}
